// src/writer/components/BookStructureDashboard.tsx
import React, { useState } from 'react';
import { ProjectState } from '../types/project';
import { FrontMatter, BackMatter, AuthorDetails, BookStructureTextElementKey } from '../types';
import { actionButtonClass, primaryButtonClass } from '../constants';
import TemplateSelector from './TemplateSelector';
import BatchGenerationModal from './BatchGenerationModal';
import SelectiveGenerationInterface from './SelectiveGenerationInterface';
import { BatchGenerationConfig, BatchGenerationProgress } from '../utils/batchGeneration';
import { useProfileImport } from '../hooks/useProfileImport';

interface BookStructureDashboardProps {
    projectState: ProjectState;
    onNavigateToSection: (section: 'author' | 'frontMatter' | 'backMatter') => void;
    onGenerateElements: () => void;
    onGenerateSelectedElements?: (selectedElements: BookStructureTextElementKey[]) => void;
    isGenerating: boolean;
    onApplyTemplate?: (templateData: Partial<ProjectState>) => void;
    onBatchGeneration?: (config: BatchGenerationConfig) => void;
    batchGenerationProgress?: BatchGenerationProgress;
    onAuthorDetailsChange?: <K extends keyof AuthorDetails>(field: K, value: AuthorDetails[K]) => void;
}

interface SectionProgress {
    name: string;
    icon: string;
    completedFields: number;
    totalFields: number;
    completionPercentage: number;
    status: 'empty' | 'partial' | 'complete';
    onClick: () => void;
}

const BookStructureDashboard: React.FC<BookStructureDashboardProps> = ({
    projectState,
    onNavigateToSection,
    onGenerateElements,
    onGenerateSelectedElements,
    isGenerating,
    onApplyTemplate,
    onBatchGeneration,
    batchGenerationProgress,
    onAuthorDetailsChange
}) => {
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);
    const [showBatchGeneration, setShowBatchGeneration] = useState(false);
    const { importFromProfile, isImporting, profileData, profileLoading, profileError } = useProfileImport();

    // Handle profile import
    const handleQuickProfileImport = async () => {
        if (!onAuthorDetailsChange) {
            console.warn('Profile import not available - onAuthorDetailsChange not provided');
            return;
        }

        try {
            const importedData = await importFromProfile();
            
            // Apply the imported data
            Object.entries(importedData).forEach(([key, value]) => {
                if (key in projectState.authorDetails) {
                    onAuthorDetailsChange(key as keyof AuthorDetails, value);
                }
            });
            
            // Navigate to author section to see the imported data
            onNavigateToSection('author');
        } catch (error) {
            console.error('Failed to import profile:', error);
        }
    };
    // Calculate completion status for each section
    const calculateSectionProgress = (): SectionProgress[] => {
        // Author Details Progress
        const authorFields = Object.values(projectState.authorDetails || {});
        const authorCompleted = authorFields.filter(field => field && typeof field === 'string' && field.trim().length > 0).length;
        const authorTotal = Object.keys(projectState.authorDetails || {}).length;
        
        // Front Matter Progress
        const frontMatterFields = Object.values(projectState.frontMatter || {});
        const frontMatterCompleted = frontMatterFields.filter(field => field && typeof field === 'string' && field.trim().length > 0).length;
        const frontMatterTotal = Object.keys(projectState.frontMatter || {}).length;
        
        // Back Matter Progress
        const backMatterFields = Object.values(projectState.backMatter || {});
        const backMatterCompleted = backMatterFields.filter(field => field && typeof field === 'string' && field.trim().length > 0).length;
        const backMatterTotal = Object.keys(projectState.backMatter || {}).length;
        
        const getStatus = (completed: number, total: number): 'empty' | 'partial' | 'complete' => {
            if (completed === 0) return 'empty';
            if (completed === total) return 'complete';
            return 'partial';
        };
        
        return [
            {
                name: 'Author Details',
                icon: '👤',
                completedFields: authorCompleted,
                totalFields: authorTotal,
                completionPercentage: Math.round((authorCompleted / Math.max(authorTotal, 1)) * 100),
                status: getStatus(authorCompleted, authorTotal),
                onClick: () => onNavigateToSection('author')
            },
            {
                name: 'Front Matter',
                icon: '📖',
                completedFields: frontMatterCompleted,
                totalFields: frontMatterTotal,
                completionPercentage: Math.round((frontMatterCompleted / Math.max(frontMatterTotal, 1)) * 100),
                status: getStatus(frontMatterCompleted, frontMatterTotal),
                onClick: () => onNavigateToSection('frontMatter')
            },
            {
                name: 'Back Matter',
                icon: '📚',
                completedFields: backMatterCompleted,
                totalFields: backMatterTotal,
                completionPercentage: Math.round((backMatterCompleted / Math.max(backMatterTotal, 1)) * 100),
                status: getStatus(backMatterCompleted, backMatterTotal),
                onClick: () => onNavigateToSection('backMatter')
            }
        ];
    };

    const sections = calculateSectionProgress();
    const overallProgress = Math.round(
        sections.reduce((acc, section) => acc + section.completionPercentage, 0) / sections.length
    );

    const getStatusColor = (status: 'empty' | 'partial' | 'complete') => {
        switch (status) {
            case 'empty': return 'bg-gray-200 text-gray-600';
            case 'partial': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'complete': return 'bg-green-100 text-green-800 border-green-300';
        }
    };

    const getStatusIcon = (status: 'empty' | 'partial' | 'complete') => {
        switch (status) {
            case 'empty': return '⚪';
            case 'partial': return '🟡';
            case 'complete': return '✅';
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-8">
            {/* Book Spine Visualization */}
            <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-800">Book Structure Overview</h3>
                    <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-600">
                            Overall Progress: <span className="font-semibold">{overallProgress}%</span>
                        </div>
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${overallProgress}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                {/* Visual Book Spine */}
                <div className="flex items-center justify-center mb-8">
                    <div className="relative">
                        {/* Book representation */}
                        <div className="w-64 h-80 bg-gradient-to-b from-blue-600 to-blue-800 rounded-lg shadow-2xl relative overflow-hidden">
                            {/* Book spine details */}
                            <div className="absolute inset-4 flex flex-col justify-between text-white">
                                <div className="text-center">
                                    <div className="text-xs opacity-75 mb-1">BOOK STRUCTURE</div>
                                    <div className="text-sm font-semibold truncate">
                                        {projectState.bookMetadata?.title || 'Untitled Book'}
                                    </div>
                                    <div className="text-xs opacity-75 mt-1 truncate">
                                        {projectState.authorDetails?.authorName || 'Author Name'}
                                    </div>
                                </div>
                                
                                {/* Section indicators on spine */}
                                <div className="space-y-2">
                                    {sections.map((section, index) => (
                                        <div key={section.name} className="flex items-center justify-between text-xs">
                                            <span className="truncate">{section.name}</span>
                                            <span>{getStatusIcon(section.status)}</span>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="text-center text-xs opacity-75">
                                    {overallProgress}% Complete
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Progress Cards */}
            <div className="grid md:grid-cols-3 gap-6">
                {sections.map((section) => (
                    <div
                        key={section.name}
                        onClick={section.onClick}
                        className={`bg-white rounded-xl shadow-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-xl hover:scale-105 border-2 ${getStatusColor(section.status)}`}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                <span className="text-2xl">{section.icon}</span>
                                <h4 className="text-lg font-semibold">{section.name}</h4>
                            </div>
                            <span className="text-2xl">{getStatusIcon(section.status)}</span>
                        </div>
                        
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span>Progress</span>
                                <span className="font-semibold">{section.completionPercentage}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${section.completionPercentage}%` }}
                                ></div>
                            </div>
                            <div className="text-xs text-gray-600">
                                {section.completedFields} of {section.totalFields} fields completed
                            </div>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <button 
                                className={`w-full text-sm ${primaryButtonClass} hover:bg-blue-700`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    section.onClick();
                                }}
                            >
                                Edit {section.name}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6">
                <h4 className="text-lg font-semibold mb-4">Quick Actions</h4>
                <div className="grid md:grid-cols-2 gap-4">
                    {/* Profile Import CTA - Show prominently if author details are empty */}
                    {(!projectState.authorDetails?.authorName || !projectState.authorDetails?.authorBio) && onAuthorDetailsChange && (
                        <div className="md:col-span-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h5 className="font-semibold mb-1">👤 Import Author Profile</h5>
                                    <p className="text-sm opacity-90">
                                        Get started instantly by importing your author details from your profile
                                    </p>
                                </div>
                                <button
                                    onClick={handleQuickProfileImport}
                                    disabled={isImporting || profileLoading}
                                    className="bg-white text-blue-600 hover:bg-gray-100 font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isImporting || profileLoading ? (
                                        <div className="flex items-center space-x-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                            <span>Importing...</span>
                                        </div>
                                    ) : (
                                        'Import Now'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Batch Generation CTA - Prominent for users with complete setup */}
                    {(projectState.authorDetails?.authorName && projectState.bookMetadata?.genreSubject) && (
                        <div className="md:col-span-2 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg p-4 text-white mb-4">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h5 className="font-semibold text-lg mb-1">🚀 Ready for Batch Generation!</h5>
                                    <p className="text-green-100 text-sm">
                                        Your project has sufficient context. Generate all front/back matter elements in one click.
                                        This will create: dedication, prologue/introduction, epilogue, about the author, and more.
                                    </p>
                                </div>
                                <button
                                    onClick={onGenerateElements}
                                    disabled={isGenerating}
                                    className={`ml-4 px-6 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-green-50 transition-colors ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {isGenerating ? (
                                        <div className="flex items-center space-x-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                            <span>Generating...</span>
                                        </div>
                                    ) : (
                                        '🎯 Select & Generate Elements'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    <button
                        className={`${primaryButtonClass} py-3`}
                        onClick={() => setShowBatchGeneration(true)}
                        disabled={isGenerating}
                    >
                        🚀 Smart Batch Generation
                    </button>
                    <button
                        onClick={onGenerateElements}
                        disabled={isGenerating}
                        className={`${actionButtonClass} py-3 ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isGenerating ? (
                            <div className="flex items-center justify-center space-x-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>Generating...</span>
                            </div>
                        ) : (
                            '✨ Generate All Elements (Legacy)'
                        )}
                    </button>
                    <button
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-3 px-4 rounded"
                        onClick={() => setShowTemplateSelector(true)}
                    >
                        📋 Apply Book Template
                    </button>

                    {/* Alternative Profile Import for when author details exist */}
                    {(projectState.authorDetails?.authorName || projectState.authorDetails?.authorBio) && onAuthorDetailsChange && (
                        <button
                            onClick={handleQuickProfileImport}
                            disabled={isImporting || profileLoading}
                            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isImporting || profileLoading ? (
                                <div className="flex items-center justify-center space-x-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Importing...</span>
                                </div>
                            ) : (
                                '👤 Update from Profile'
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Tips and Guidance */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h4 className="text-lg font-semibold text-blue-800 mb-3">📝 Professional Book Structure Tips</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-700">
                    <div>
                        <h5 className="font-medium mb-2">Essential Front Matter:</h5>
                        <ul className="space-y-1 text-xs">
                            <li>• Title Page (required)</li>
                            <li>• Copyright Page (required)</li>
                            <li>• Table of Contents (recommended)</li>
                            <li>• Dedication (optional)</li>
                            <li>• Acknowledgments (recommended)</li>
                        </ul>
                    </div>
                    <div>
                        <h5 className="font-medium mb-2">Professional Back Matter:</h5>
                        <ul className="space-y-1 text-xs">
                            <li>• About the Author (recommended)</li>
                            <li>• Author's Note (optional)</li>
                            <li>• Bibliography (for non-fiction)</li>
                            <li>• Index (for non-fiction)</li>
                            <li>• Reading Group Guide (optional)</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Selective Generation Interface */}
            {onGenerateSelectedElements && (
                <SelectiveGenerationInterface
                    projectState={projectState}
                    onGenerateSelected={onGenerateSelectedElements as (selectedElements: import('../types').BookStructureElementKey[]) => void}
                    isGenerating={isGenerating}
                />
            )}

            {/* Template Selector Modal */}
            {onApplyTemplate && (
                <TemplateSelector
                    isOpen={showTemplateSelector}
                    onClose={() => setShowTemplateSelector(false)}
                    onApplyTemplate={onApplyTemplate}
                    currentProjectState={projectState}
                />
            )}

            {/* Batch Generation Modal */}
            {onBatchGeneration && (
                <BatchGenerationModal
                    isOpen={showBatchGeneration}
                    onClose={() => setShowBatchGeneration(false)}
                    projectState={projectState}
                    onStartGeneration={onBatchGeneration}
                    isGenerating={isGenerating}
                    generationProgress={batchGenerationProgress}
                />
            )}
        </div>
    );
};

export default BookStructureDashboard;